import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Alert,
  LinearProgress,
  Paper,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  useTheme
} from '@mui/material';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { storage } from '../../firebase/config';
import EncounterDocumentsService from '../../services/encounterDocumentsService';
import {
  UploadFile as UploadFileIcon,
  AttachFile as AttachFileIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const DOCUMENT_TYPES = [
  'Lab Report',
  'Imaging Report',
  'Consultation Note',
  'Procedure Note',
  'Discharge Summary',
  'Consent Form',
  'Insurance Document',
  'Other'
];

const EncounterDocumentsTab = ({ encounter, patient, onDocumentsChange }) => {
  const theme = useTheme();
  const [documents, setDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    documentType: '',
    tags: ''
  });
  const navigate = useNavigate();

  const loadDocuments = useCallback(async () => {
    try {
      const documentsService = new EncounterDocumentsService();
      const documentsList = await documentsService.getEncounterDocuments(encounter.id);
      setDocuments(documentsList);
      if (onDocumentsChange) {
        onDocumentsChange(documentsList);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      setError('Failed to load documents');
    }
  }, [encounter?.id, onDocumentsChange]);

  useEffect(() => {
    if (encounter?.id) {
      loadDocuments();
    }
  }, [encounter?.id, loadDocuments]);

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    if (!encounter?.id) {
      setError('Please save the encounter before uploading documents.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const uploadPromises = files.map(async (file) => {
        // Validate file size (10MB limit)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
          throw new Error(`File ${file.name} is too large. Maximum size is 10MB.`);
        }

        // Generate unique filename
        const timestamp = new Date().getTime();
        const filename = `${timestamp}-${file.name}`;
        
        // Create storage reference - upload to Firebase Storage
        const storageRef = ref(storage, `encounter-documents/${encounter.id}/${filename}`);
        
        // Upload file to Firebase Storage
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        // Return metadata for Firestore
        return {
          name: file.name,
          originalName: file.name,
          url: downloadURL,
          size: file.size,
          type: file.type,
          uploadedAt: new Date(),
          encounterId: encounter.id,
          patientId: patient?.id || encounter.patientId,
          storagePath: `encounter-documents/${encounter.id}/${filename}`,
          documentType: 'Other',
          description: '',
          tags: []
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      
      // Store document metadata using the service
      const documentsService = new EncounterDocumentsService();
      const documentPromises = uploadedFiles.map(async (fileData) => {
        const document = await documentsService.createEncounterDocument(encounter.id, {
          ...fileData,
          uploadedAt: new Date(),
        });
        return document;
      });

      const documentsWithIds = await Promise.all(documentPromises);
      setDocuments(prev => [...documentsWithIds, ...prev]);
      
      setSuccess(`Successfully uploaded ${uploadedFiles.length} document(s).`);
      
      // Notify parent component
      if (onDocumentsChange) {
        onDocumentsChange([...documentsWithIds, ...documents]);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      setError(`Failed to upload documents: ${error.message}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Clear the input
      event.target.value = '';
    }
  };

  const handleUploadClick = () => {
    document.getElementById('encounter-document-upload-input').click();
  };

  const handleRemoveDocument = async (documentId, storagePath) => {
    try {
      // Delete from backend API (metadata)
      const documentsService = new EncounterDocumentsService();
      await documentsService.deleteEncounterDocument(documentId);
      
      // Delete from Firebase Storage (actual file)
      if (storagePath) {
        const storageRef = ref(storage, storagePath);
        await deleteObject(storageRef);
      }

      // Remove from local state
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
      
      setSuccess('Document deleted successfully.');
      
      // Notify parent component
      if (onDocumentsChange) {
        onDocumentsChange(documents.filter(doc => doc.id !== documentId));
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      setError('Failed to delete document.');
    }
  };

  const handleViewDocument = (url) => {
    if (url.startsWith('http')) {
      window.open(url, '_blank');
    } else {
      navigate(url);
    }
  };

  const handleEditDocument = (document) => {
    setEditingDocument(document);
    setEditFormData({
      name: document.name || '',
      description: document.description || '',
      documentType: document.documentType || 'Other',
      tags: document.tags ? document.tags.join(', ') : ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const updatedData = {
        name: editFormData.name,
        description: editFormData.description,
        documentType: editFormData.documentType,
        tags: editFormData.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        updatedAt: new Date()
      };

      // Update metadata using the service
      const documentsService = new EncounterDocumentsService();
      await documentsService.updateEncounterDocument(editingDocument.id, updatedData);
      
      // Update local state
      setDocuments(prev => prev.map(doc => 
        doc.id === editingDocument.id 
          ? { ...doc, ...updatedData }
          : doc
      ));
      
      setEditDialogOpen(false);
      setEditingDocument(null);
      setSuccess('Document updated successfully.');
      
      // Notify parent component
      if (onDocumentsChange) {
        onDocumentsChange(documents.map(doc => 
          doc.id === editingDocument.id 
            ? { ...doc, ...updatedData }
            : doc
        ));
      }
    } catch (error) {
      console.error('Error updating document:', error);
      setError('Failed to update document.');
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) return <DescriptionIcon color="primary" />;
    if (fileType.includes('image')) return <AttachFileIcon color="secondary" />;
    return <AttachFileIcon color="primary" />;
  };

  return (
    <Box>
      {/* Upload Section */}
      <Box sx={{ mb: 3 }}>
        <Button 
          variant="outlined" 
          onClick={handleUploadClick}
          disabled={isUploading || !encounter?.id}
          startIcon={isUploading ? <LinearProgress size={16} /> : <UploadFileIcon />}
          sx={{ mb: 2 }}
        >
          {isUploading ? 'Uploading...' : 'Upload Documents'}
        </Button>
        <input
          id="encounter-document-upload-input"
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
        />
        
        {isUploading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Uploading documents...
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
      </Box>

      {/* Documents List */}
      {documents.length > 0 ? (
        <Box>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Encounter Documents ({documents.length})
          </Typography>
          <Grid container spacing={2}>
            {documents.map((doc) => (
              <Grid item xs={12} md={6} lg={4} key={doc.id}>
                <Paper 
                  sx={{ 
                    p: 2, 
                    border: '1px solid', 
                    borderColor: 'divider', 
                    borderRadius: 2,
                    '&:hover': {
                      boxShadow: theme.shadows[4],
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      {getFileIcon(doc.type)}
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                        {doc.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleViewDocument(doc.url)}
                        sx={{ color: 'primary.main' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEditDocument(doc)}
                        sx={{ color: 'info.main' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveDocument(doc.id, doc.storagePath)}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  {doc.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                      {doc.description}
                    </Typography>
                  )}
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {doc.documentType && (
                      <Chip 
                        label={doc.documentType} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: '20px' }}
                      />
                    )}
                    {doc.tags && doc.tags.map((tag, index) => (
                      <Chip 
                        key={index}
                        label={tag} 
                        size="small" 
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: '20px' }}
                      />
                    ))}
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem' }}>
                    {doc.type} • {Math.round(doc.size / 1024)} KB
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.75rem' }}>
                    {doc.uploadedAt && format(new Date(doc.uploadedAt.seconds ? doc.uploadedAt.toDate() : doc.uploadedAt), 'MMM dd, yyyy HH:mm')}
                  </Typography>
                  
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleViewDocument(doc.url)}
                      sx={{ fontSize: '0.75rem', py: 0.5 }}
                    >
                      Download
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
            No documents uploaded for this encounter yet
          </Typography>
        </Box>
      )}

      {/* Edit Document Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Document</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Document Name"
              value={editFormData.name}
              onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={editFormData.description}
              onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={editFormData.documentType}
                onChange={(e) => setEditFormData(prev => ({ ...prev, documentType: e.target.value }))}
                label="Document Type"
              >
                {DOCUMENT_TYPES.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Tags (comma-separated)"
              value={editFormData.tags}
              onChange={(e) => setEditFormData(prev => ({ ...prev, tags: e.target.value }))}
              placeholder="lab, urgent, follow-up"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EncounterDocumentsTab; 